import { useEffect, useRef, type RefObject } from 'react';

interface UseDialogA11yOptions {
  isOpen: boolean;
  onClose: () => void;
  dialogRef: RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Shared modal-dialog accessibility behavior: Escape-to-close, focus moved into the
 * dialog on open, Tab/Shift+Tab trapped within the dialog's focusable elements, and
 * focus restored to whatever had it before the dialog opened.
 *
 * The dialog container itself must be given `tabIndex={-1}` so it's a valid focus
 * target when there's no focusable child to land on.
 */
export function useDialogA11y({ isOpen, onClose, dialogRef }: UseDialogA11yOptions): void {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Every call site passes an inline onClose (new identity per render). If the effect
  // depended on onClose directly, any unrelated re-render of the host while the dialog
  // is open would tear the effect down and re-run it — restoring focus to whatever had
  // it pre-open, then immediately stealing it back into the dialog. A ref keeps the
  // effect's identity stable across renders while still calling the latest onClose.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const dialogEl = dialogRef.current;
    if (dialogEl) {
      const focusable = getFocusable(dialogEl);
      (focusable[0] ?? dialogEl).focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (e.key !== 'Tab') return;
      const el = dialogRef.current;
      if (!el) return;

      const focusable = getFocusable(el);
      if (focusable.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [isOpen, dialogRef]);
}
